import { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Plus, Calendar } from "lucide-react";

const WEEKDAY = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTH_LONG = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

// Local-time helpers (calendar is shown in the admin's local timezone).
const sameDay = (a, b) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

// Effective placement date for an article:
//  - If publish_at is set, use it (works for past=auto-published, future=scheduled).
//  - Else use created_at (drafts & legacy live posts).
const effectiveDate = (a) => {
  const iso = a.publish_at || a.created_at;
  return iso ? new Date(iso) : null;
};

// Status for chip coloring/labelling. We compute on the fly so a scheduled
// post auto-flips to "live" once its publish_at is in the past.
const statusOf = (a, now = Date.now()) => {
  if (!a.published) return "draft";
  if (a.publish_at && new Date(a.publish_at).getTime() > now) return "scheduled";
  return "live";
};

const STATUS_COLORS = {
  live: "pcs-bg-pink text-white",
  scheduled: "pcs-bg-blue text-white",
  draft: "bg-gray-200 text-black",
};

export default function ArticleCalendar({ articles, onSelectArticle, onCreateNew }) {
  // Month being viewed. Start on the current month.
  const [view, setView] = useState(() => {
    const d = new Date();
    return { year: d.getFullYear(), month: d.getMonth() };
  });

  const goPrev = () =>
    setView((v) =>
      v.month === 0 ? { year: v.year - 1, month: 11 } : { ...v, month: v.month - 1 }
    );
  const goNext = () =>
    setView((v) =>
      v.month === 11 ? { year: v.year + 1, month: 0 } : { ...v, month: v.month + 1 }
    );
  const goToday = () => {
    const d = new Date();
    setView({ year: d.getFullYear(), month: d.getMonth() });
  };

  // Group articles by effective YYYY-MM-DD (in local time) so cell lookups are O(1).
  const byDay = useMemo(() => {
    const map = new Map();
    for (const a of articles) {
      const d = effectiveDate(a);
      if (!d) continue;
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(a);
    }
    // Sort each day's chip list: scheduled first, then live, then drafts.
    const order = { scheduled: 0, live: 1, draft: 2 };
    for (const list of map.values()) {
      list.sort((x, y) => order[statusOf(x)] - order[statusOf(y)]);
    }
    return map;
  }, [articles]);

  // Build the 6-row x 7-col grid for the current month, starting from the
  // Sunday of the first calendar row (which may be in the previous month).
  const cells = useMemo(() => {
    const first = new Date(view.year, view.month, 1);
    const startDay = first.getDay(); // 0=Sun..6=Sat
    const start = new Date(view.year, view.month, 1 - startDay);
    const out = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      out.push(d);
    }
    return out;
  }, [view]);

  // Counts for the month header
  const monthStats = useMemo(() => {
    const stats = { live: 0, scheduled: 0, draft: 0 };
    for (const a of articles) {
      const d = effectiveDate(a);
      if (!d || d.getFullYear() !== view.year || d.getMonth() !== view.month) continue;
      stats[statusOf(a)] += 1;
    }
    return stats;
  }, [articles, view]);

  const today = new Date();

  return (
    <div className="space-y-3" data-testid="article-calendar">
      {/* Month nav */}
      <div className="brut bg-white p-3 flex flex-wrap items-center gap-2">
        <Calendar className="w-4 h-4 ml-1" strokeWidth={2.5} />
        <button
          type="button"
          onClick={goPrev}
          data-testid="calendar-prev"
          aria-label="Previous month"
          className="brut bg-white p-1.5 brut-press"
        >
          <ChevronLeft className="w-4 h-4" strokeWidth={3} />
        </button>
        <div className="font-display text-xl tracking-tight min-w-[170px]">
          {MONTH_LONG[view.month]} {view.year}
        </div>
        <button
          type="button"
          onClick={goNext}
          data-testid="calendar-next"
          aria-label="Next month"
          className="brut bg-white p-1.5 brut-press"
        >
          <ChevronRight className="w-4 h-4" strokeWidth={3} />
        </button>
        <button
          type="button"
          onClick={goToday}
          data-testid="calendar-today"
          className="brut bg-white px-2 py-1 font-mono uppercase text-[10px] tracking-widest brut-press"
        >
          Today
        </button>

        <div className="ml-auto flex flex-wrap items-center gap-3 font-mono uppercase text-[10px] tracking-widest opacity-80">
          <span className="inline-flex items-center gap-1.5">
            <span className="inline-block w-2 h-2 pcs-bg-pink" />
            Live · {monthStats.live}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="inline-block w-2 h-2 pcs-bg-blue" />
            Scheduled · {monthStats.scheduled}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="inline-block w-2 h-2 bg-gray-400" />
            Draft · {monthStats.draft}
          </span>
        </div>
      </div>

      {/* Weekday header */}
      <div className="grid grid-cols-7 gap-0 brut bg-white">
        {WEEKDAY.map((w) => (
          <div
            key={w}
            className="p-2 font-mono uppercase text-[10px] tracking-widest opacity-70 border-r-2 border-b-2 border-black/20 last:border-r-0 text-center"
          >
            {w}
          </div>
        ))}

        {/* 42 cells */}
        {cells.map((d, i) => {
          const inMonth = d.getMonth() === view.month;
          const isToday = sameDay(d, today);
          const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
          const items = byDay.get(key) || [];
          const visible = items.slice(0, 3);
          const overflow = items.length - visible.length;
          return (
            <div
              key={key}
              className={`min-h-[110px] border-r-2 border-b-2 border-black/20 last:border-r-0 p-1.5 flex flex-col gap-1 group relative ${
                inMonth ? "bg-white" : "bg-gray-50 opacity-60"
              } ${isToday ? "ring-2 ring-inset ring-[#FF3366]" : ""}`}
              data-testid={`calendar-cell-${key}`}
            >
              <div className="flex items-center justify-between">
                <span
                  className={`font-mono text-xs ${
                    isToday ? "font-bold pcs-text-pink" : "opacity-70"
                  }`}
                >
                  {d.getDate()}
                </span>
                {items.length === 0 && inMonth && (
                  <button
                    type="button"
                    onClick={onCreateNew}
                    data-testid={`calendar-add-${key}`}
                    className="opacity-0 group-hover:opacity-100 transition-opacity brut bg-white p-0.5 brut-press"
                    aria-label={`Add article on ${d.toDateString()}`}
                    title="New article"
                  >
                    <Plus className="w-3 h-3" strokeWidth={3} />
                  </button>
                )}
              </div>

              {visible.map((a) => {
                const status = statusOf(a);
                return (
                  <button
                    type="button"
                    key={a.slug}
                    onClick={() => onSelectArticle(a.slug)}
                    title={`${a.title} (${status})`}
                    data-testid={`calendar-chip-${a.slug}`}
                    className={`text-left px-1.5 py-1 brut-press text-[11px] font-bold truncate leading-tight border-2 border-black ${STATUS_COLORS[status]}`}
                  >
                    {a.title}
                  </button>
                );
              })}
              {overflow > 0 && (
                <div className="font-mono text-[10px] uppercase tracking-widest opacity-60 pl-1">
                  +{overflow} more
                </div>
              )}
            </div>
          );
        })}
      </div>

      <p className="text-xs opacity-60">
        Articles appear on their <code className="font-mono">publish_at</code>{" "}
        date when scheduled, otherwise on their <code className="font-mono">created_at</code> date. Click a chip to jump
        into the editor.
      </p>
    </div>
  );
}
